import { useState } from "react";
import {
  deriveStatus,
  formatTimeRange,
  isClosedNoFeedback,
} from "../../utils/openHouseHelpers";
import moment from "moment";

const STATUS_PILL = {
  Scheduled: "bg-blue-50 text-blue-700",
  Completed: "bg-emerald-50 text-emerald-700",
  Cancelled: "bg-slate-100 text-slate-500",
};

// Shared derivation so the table row and the mobile card agree on status.
function statusInfo(oh) {
  const status = deriveStatus(oh);
  // Occupied-home flow: the OH is saved but seller/host notifications are on
  // hold until the user confirms the seller verbally OK'd the time.
  const awaitingSeller = !!oh.pendingSellerApproval && status === "Scheduled";
  return { status, awaitingSeller };
}

function StatusPill({ oh }) {
  const { status, awaitingSeller } = statusInfo(oh);
  if (awaitingSeller) {
    return (
      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 whitespace-nowrap">
        Awaiting Seller OK
      </span>
    );
  }
  return (
    <span
      className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_PILL[status]}`}
    >
      {status}
    </span>
  );
}

function FeedbackSummary({ oh }) {
  if (!oh.feedback) return <span className="text-slate-400 italic">—</span>;
  return (
    <>
      <span className="font-semibold">{oh.feedback.groups || 0}</span> groups ·{" "}
      <span className="font-semibold">{oh.feedback.secondShowings || 0}</span> 2nd
      showings
    </>
  );
}

// Action buttons + the ⋮ overflow menu. Used by both the desktop row and the
// mobile card, so each instance owns its own menu state.
function RowActions({
  oh,
  align = "end",
  onAddFeedback,
  onEditFeedback,
  onEdit,
  onCancel,
  onSendRecap,
  onResendInvite,
  onDelete,
  onCopyLink,
  onSendInvites,
  onCheckin,
  onMarkComplete,
}) {
  const { status, awaitingSeller } = statusInfo(oh);
  const closed = isClosedNoFeedback(oh);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="relative">
      <div
        className={`flex items-center gap-2 ${
          // Desktop rows keep one straight right-aligned line so buttons sit
          // flush across rows; the mobile card may wrap.
          align === "end" ? "justify-end flex-nowrap" : "justify-start flex-wrap"
        }`}
      >
        {awaitingSeller && (
          <button
            onClick={() => onSendInvites && onSendInvites(oh)}
            title="Send open-house notifications to the seller and hosting agent"
            className="text-amber-800 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-xs font-semibold px-2.5 py-1 rounded-md whitespace-nowrap"
          >
            Send Now
          </button>
        )}
        {status === "Scheduled" && !awaitingSeller && (
          <button
            onClick={() => onAddFeedback(oh)}
            className="text-slate-800 border border-slate-300 hover:bg-slate-100 text-xs font-semibold px-2.5 py-1 rounded-md whitespace-nowrap"
          >
            Add Feedback
          </button>
        )}
        {status === "Completed" && !closed && (
          <button
            onClick={() => onSendRecap(oh)}
            className={`text-xs font-semibold px-2.5 py-1 rounded-md whitespace-nowrap ${
              oh.recapSentAt
                ? "text-slate-700 border border-slate-200 hover:bg-slate-50"
                : "text-slate-800 border border-slate-300 hover:bg-slate-100"
            }`}
          >
            {oh.recapSentAt ? "Re-send Recap" : "Send Recap"}
          </button>
        )}
        {/* Closed with nothing to report — nothing left to restart, but any
            kiosk sign-ins from the day still need sorting, so the Check-In
            Center stays reachable under a quieter label. */}
        <button
          onClick={() => onCheckin(oh)}
          className="text-slate-800 border border-slate-300 hover:bg-slate-100 text-xs font-semibold px-2.5 py-1 rounded-md whitespace-nowrap"
        >
          {closed ? "View Sign-Ins" : "Start Open House"}
        </button>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="text-slate-500 hover:text-slate-700 px-1.5 py-1 rounded-md shrink-0"
          aria-label="More actions"
        >
          ⋮
        </button>
      </div>
      {menuOpen && (
        <div
          className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 text-xs min-w-[200px]"
          onMouseLeave={() => setMenuOpen(false)}
        >
          {status === "Scheduled" && (
            <>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onEdit(oh);
                }}
                className="block w-full text-left px-3 py-2 hover:bg-slate-50"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onCopyLink && onCopyLink(oh);
                }}
                className="block w-full text-left px-3 py-2 hover:bg-slate-50"
              >
                Copy public feedback link
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onResendInvite(oh);
                }}
                className="block w-full text-left px-3 py-2 hover:bg-slate-50"
              >
                Re-send invite to hosting agent
              </button>
              {/* Done, nothing worth writing down. Without this an open house
                  that produced no feedback stayed "Scheduled" forever. */}
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onMarkComplete && onMarkComplete(oh);
                }}
                className="block w-full text-left px-3 py-2 hover:bg-slate-50"
              >
                Mark complete (no feedback)
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onCancel(oh);
                }}
                className="block w-full text-left px-3 py-2 hover:bg-red-50 text-red-600"
              >
                Cancel
              </button>
            </>
          )}
          {status === "Completed" && !closed && (
            <button
              onClick={() => {
                setMenuOpen(false);
                onEditFeedback(oh);
              }}
              className="block w-full text-left px-3 py-2 hover:bg-slate-50"
            >
              Edit feedback
            </button>
          )}
          <button
            onClick={() => {
              setMenuOpen(false);
              onDelete && onDelete(oh);
            }}
            className="block w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 border-t border-slate-100"
          >
            Delete permanently
          </button>
        </div>
      )}
    </div>
  );
}

export default function OpenHousesTable({
  rows,
  onAddFeedback,
  onEditFeedback,
  onEdit,
  onCancel,
  onSendRecap,
  onResendInvite,
  onDelete,
  onCopyLink,
  onSendInvites,
  onCheckin,
  onMarkComplete,
}) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-10 text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
        No open houses match this filter.
      </div>
    );
  }

  const handlers = {
    onAddFeedback,
    onEditFeedback,
    onEdit,
    onCancel,
    onSendRecap,
    onResendInvite,
    onDelete,
    onCopyLink,
    onSendInvites,
    onCheckin,
    onMarkComplete,
  };

  return (
    <>
      {/* Mobile: stacked cards (a 6-column table can't fit a phone width). */}
      <div className="md:hidden space-y-3">
        {rows.map((oh) => (
          <div
            key={oh.id}
            className="border border-slate-200 rounded-lg bg-white p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">
                  {(oh.propertyAddress || "").split(",")[0] || "—"}
                </div>
                {oh.sellerName && (
                  <div className="text-xs text-slate-500 truncate">
                    {oh.sellerName}
                  </div>
                )}
              </div>
              <div className="shrink-0">
                <StatusPill oh={oh} />
              </div>
            </div>

            <div className="mt-3 space-y-2 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">
                  Date &amp; Time
                </div>
                <div className="text-slate-900">
                  {oh.date ? moment(oh.date).format("MM/DD/YYYY") : "—"}
                  <span className="text-slate-500">
                    {oh.startTime || oh.endTime
                      ? ` · ${formatTimeRange(oh.startTime, oh.endTime)}`
                      : ""}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">
                  Hosting Agent
                </div>
                <div className="text-slate-900">
                  {oh.hostingAgentName || "—"}
                </div>
                {oh.hostingAgentEmail && (
                  <div className="text-xs text-slate-500 break-all">
                    {oh.hostingAgentEmail}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">
                  Feedback
                </div>
                <div className="text-xs text-slate-700">
                  <FeedbackSummary oh={oh} />
                </div>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-slate-100">
              <RowActions oh={oh} align="start" {...handlers} />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: the full table, now safely scrollable if it ever overflows. */}
      <div className="hidden md:block border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Property</th>
              <th className="text-left px-4 py-2.5 font-semibold">
                Date &amp; Time
              </th>
              <th className="text-left px-4 py-2.5 font-semibold">
                Hosting Agent
              </th>
              <th className="text-left px-4 py-2.5 font-semibold">Status</th>
              <th className="text-left px-4 py-2.5 font-semibold">Feedback</th>
              <th
                className="text-right px-4 py-2.5 font-semibold"
                aria-label="Actions"
              ></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((oh) => (
              <tr
                key={oh.id}
                className="border-t border-slate-100 hover:bg-slate-50/60"
              >
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">
                    {(oh.propertyAddress || "").split(",")[0] || "—"}
                  </div>
                  <div className="text-xs text-slate-500">
                    {oh.sellerName || ""}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-slate-900">
                    {oh.date ? moment(oh.date).format("MM/DD/YYYY") : "—"}
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatTimeRange(oh.startTime, oh.endTime)}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-slate-900">
                    {oh.hostingAgentName || "—"}
                  </div>
                  <div className="text-xs text-slate-500">
                    {oh.hostingAgentEmail || ""}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <StatusPill oh={oh} />
                </td>
                <td className="px-4 py-3 text-slate-700 text-xs">
                  <FeedbackSummary oh={oh} />
                </td>
                <td className="px-4 py-3">
                  <RowActions oh={oh} align="end" {...handlers} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
